import React, { useState, useEffect } from 'react';
import { 
    StarIcon, 
    HeartIcon, 
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PhotoIcon,
    UserCircleIcon,
    CheckBadgeIcon,
    TrashIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import { 
    StarIcon as StarIconSolid,
    HeartIcon as HeartIconSolid 
} from '@heroicons/react/24/solid';
import { reviewsApi } from '../../utils/api';

/**
 * Composant pour afficher la liste des avis d'un produit
 * Inclut pagination, filtrage et gestion des images
 */
const ReviewsList = ({ productId, onStatsUpdate }) => {
    // États pour les avis et la pagination
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0
    });

    // États pour les filtres et le tri
    const [filters, setFilters] = useState({
        rating: null,
        sortBy: 'created_at',
        sortOrder: 'DESC'
    });

    // État pour la lightbox d'images
    const [lightbox, setLightbox] = useState({
        isOpen: false,
        images: [],
        currentIndex: 0
    });

    // États pour la suppression d'avis
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        reviewId: null,
        isDeleting: false
    });

    // Charger les avis
    const fetchReviews = async (page = 1, newFilters = filters) => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page,
                pageSize: pagination.pageSize,
                ...newFilters
            };

            const response = await reviewsApi.getProductReviews(productId, params);
            
            // Debug temporaire
            console.log('🔍 ReviewsList - Réponse API:', response);
            console.log('🔍 ReviewsList - ProductId:', productId);
            console.log('🔍 ReviewsList - Params:', params);
            
            let reviewsData = [];
            let paginationData = { page: 1, pageSize: 10, totalCount: 0, totalPages: 0 };

            if (response.data && response.data.data && response.data.data.reviews) {
                reviewsData = response.data.data.reviews;
                paginationData = response.data.data.pagination;
            } else if (response.data && response.data.reviews) {
                reviewsData = response.data.reviews;
                paginationData = response.data.pagination;
            } else {
                console.log('🔍 Structure de réponse inattendue:', response);
                reviewsData = [];
            }

            setReviews(reviewsData);
            setPagination(paginationData);
                
            // Mettre à jour les stats dans le composant parent
            if (onStatsUpdate && paginationData.totalCount !== undefined) {
                const totalReviews = paginationData.totalCount;
                const averageRating = totalReviews > 0 && reviewsData.length > 0
                    ? reviewsData.reduce((sum, r) => sum + r.rating, 0) / reviewsData.length
                    : 0;
                
                onStatsUpdate({
                    totalReviews,
                    averageRating: averageRating.toFixed(1)
                });
            }
        } catch (err) {
            console.error('Erreur chargement avis:', err);
            setError(err.message || 'Erreur lors du chargement des avis');
        } finally {
            setLoading(false);
        }
    };

    // Effet pour charger les avis au montage et lors des changements
    useEffect(() => {
        if (productId) {
            fetchReviews(1, filters);
        }
    }, [productId, filters]);

    // Gérer le changement de filtre de notation
    const handleRatingFilter = (rating) => {
        const newFilters = {
            ...filters,
            rating: filters.rating === rating ? null : rating
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Gérer le changement de tri
    const handleSortChange = (sortBy) => {
        const newFilters = {
            ...filters,
            sortBy,
            sortOrder: filters.sortBy === sortBy && filters.sortOrder === 'DESC' ? 'ASC' : 'DESC'
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Gérer le changement de page
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
            fetchReviews(newPage, filters);
        }
    };

    // Marquer un avis comme utile
    const handleMarkHelpful = async (reviewId) => {
        try {
            await reviewsApi.markHelpful(reviewId);
            // Recharger les avis pour mettre à jour le compteur
            fetchReviews(pagination.page, filters);
        } catch (err) {
            console.error('Erreur marquer utile:', err);
        }
    };

    // Ouvrir la lightbox pour les images
    const openLightbox = (images, startIndex = 0) => {
        setLightbox({
            isOpen: true,
            images: images.filter(img => img && img.image_url),
            currentIndex: startIndex
        });
    };

    // Fermer la lightbox
    const closeLightbox = () => {
        setLightbox({ isOpen: false, images: [], currentIndex: 0 });
    };

    // Navigation dans la lightbox
    const navigateLightbox = (direction) => {
        const newIndex = direction === 'next' 
            ? (lightbox.currentIndex + 1) % lightbox.images.length
            : (lightbox.currentIndex - 1 + lightbox.images.length) % lightbox.images.length;
        setLightbox(prev => ({ ...prev, currentIndex: newIndex }));
    };

    // Ouvrir le modal de suppression
    const openDeleteModal = (reviewId) => {
        setDeleteModal({
            isOpen: true,
            reviewId: reviewId,
            isDeleting: false
        });
    };

    // Fermer le modal de suppression
    const closeDeleteModal = () => {
        setDeleteModal({
            isOpen: false,
            reviewId: null,
            isDeleting: false
        });
    };

    // Supprimer un avis
    const handleDeleteReview = async () => {
        try {
            setDeleteModal(prev => ({ ...prev, isDeleting: true }));
            
            await reviewsApi.deleteReview(deleteModal.reviewId);
            
            // Recharger les avis
            await fetchReviews(pagination.page, filters);
            
            // Fermer le modal
            closeDeleteModal();
            
        } catch (error) {
            console.error('Erreur suppression avis:', error);
            alert('Erreur lors de la suppression de l\'avis');
        } finally {
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    // Rendu des étoiles
    const renderStars = (rating, className = "h-4 w-4") => {
        return (
            <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="relative">
                        {star <= rating ? (
                            <StarIconSolid className={`${className} text-yellow-400`} />
                        ) : (
                            <StarIcon className={`${className} text-gray-300`} />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // Formater la date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8 text-red-600">
                <p>Erreur: {error}</p>
                <button 
                    onClick={() => fetchReviews(pagination.page, filters)}
                    className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Filtres et tri */}
            <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    {/* Filtres par étoiles */}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-medium text-gray-700 mr-2">Filtrer:</span>
                        {[5, 4, 3, 2, 1].map(rating => (
                            <button
                                key={rating}
                                onClick={() => handleRatingFilter(rating)}
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    filters.rating === rating
                                        ? 'bg-primary-100 text-primary-800 border border-primary-300'
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {renderStars(rating, "h-3 w-3")}
                                <span className="ml-1">{rating}</span>
                            </button>
                        ))}
                        {filters.rating && (
                            <button
                                onClick={() => handleRatingFilter(null)}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                            >
                                Tout voir
                            </button>
                        )}
                    </div>

                    {/* Options de tri */}
                    <div className="flex gap-2">
                        <span className="text-sm font-medium text-gray-700">Trier par:</span>
                        {[
                            { key: 'created_at', label: 'Date' },
                            { key: 'rating', label: 'Note' },
                            { key: 'helpful_count', label: 'Utilité' }
                        ].map(sort => (
                            <button
                                key={sort.key}
                                onClick={() => handleSortChange(sort.key)}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                    filters.sortBy === sort.key
                                        ? 'bg-primary-100 text-primary-800'
                                        : 'text-gray-600 hover:text-primary-600'
                                }`}
                            >
                                {sort.label}
                                {filters.sortBy === sort.key && (
                                    <span className="ml-1">
                                        {filters.sortOrder === 'DESC' ? '↓' : '↑'}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Liste des avis */}
            {reviews.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    <StarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium mb-2">Aucun avis</h3>
                    <p className="text-sm">
                        {filters.rating 
                            ? `Aucun avis avec ${filters.rating} étoile${filters.rating > 1 ? 's' : ''}`
                            : 'Soyez le premier à laisser un avis sur ce produit'
                        }
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {reviews.map((review) => (
                        <div key={review.review_id} className="bg-white border border-gray-200 rounded-lg p-6">
                            {/* En-tête de l'avis */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                        {review.is_anonymous ? (
                                            <UserCircleIcon className="h-10 w-10 text-gray-400" />
                                        ) : (
                                            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                                <span className="text-primary-600 font-medium text-sm">
                                                    {(review.customer_name || 'A')[0].toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <h4 className="font-medium text-gray-900">
                                                {review.customer_name || 'Client anonyme'}
                                            </h4>
                                            {review.is_verified_purchase && (
                                                <CheckBadgeIcon className="h-4 w-4 text-green-500" title="Achat vérifié" />
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2 mt-1">
                                            {renderStars(review.rating)}
                                            <span className="text-sm text-gray-500 flex items-center">
                                                <CalendarIcon className="h-4 w-4 mr-1" />
                                                {formatDate(review.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Titre de l'avis */}
                            {review.title && (
                                <h5 className="font-medium text-gray-900 mb-2">{review.title}</h5>
                            )}

                            {/* Commentaire */}
                            {review.comment && (
                                <p className="text-gray-700 mb-4 leading-relaxed">{review.comment}</p>
                            )}

                            {/* Images de l'avis */}
                            {review.images && review.images.length > 0 && (
                                <div className="mb-4">
                                    <div className="flex flex-wrap gap-2">
                                        {review.images.slice(0, 4).map((image, index) => (
                                            <button
                                                key={image.id}
                                                onClick={() => openLightbox(review.images, index)}
                                                className="relative group"
                                            >
                                                <img
                                                    src={image.image_url}
                                                    alt={image.alt_text}
                                                    className="w-20 h-20 object-cover rounded-lg border border-gray-200 group-hover:border-primary-300 transition-colors"
                                                />
                                                {index === 3 && review.images.length > 4 && (
                                                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                                                        <span className="text-white font-medium text-sm">
                                                            +{review.images.length - 4}
                                                        </span>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => handleMarkHelpful(review.review_id)}
                                        className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
                                    >
                                        <HeartIcon className="h-4 w-4 mr-1" />
                                        Utile ({review.helpful_count})
                                    </button>
                                    
                                    <button
                                        onClick={() => openDeleteModal(review.review_id)}
                                        className="inline-flex items-center text-sm text-red-600 hover:text-red-700 transition-colors"
                                        title="Supprimer cet avis"
                                    >
                                        <TrashIcon className="h-4 w-4 mr-1" />
                                        Supprimer
                                    </button>
                                </div>
                                
                                {review.is_verified_purchase && (
                                    <span className="inline-flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                        <CheckBadgeIcon className="h-3 w-3 mr-1" />
                                        Achat vérifié
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between py-4">
                    <div className="text-sm text-gray-700">
                        Page {pagination.page} sur {pagination.totalPages} 
                        ({pagination.totalCount} avis)
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            Précédent
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Suivant
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                        </button>
                    </div>
                </div>
            )}

            {/* Lightbox pour les images */}
            {lightbox.isOpen && lightbox.images.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
                    <div className="relative max-w-4xl max-h-full p-4">
                        {/* Bouton fermer */}
                        <button
                            onClick={closeLightbox}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300 z-10"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Image */}
                        <img
                            src={lightbox.images[lightbox.currentIndex]?.image_url}
                            alt={lightbox.images[lightbox.currentIndex]?.alt_text}
                            className="max-w-full max-h-full object-contain"
                        />

                        {/* Navigation */}
                        {lightbox.images.length > 1 && (
                            <>
                                <button
                                    onClick={() => navigateLightbox('prev')}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
                                >
                                    <ChevronLeftIcon className="h-8 w-8" />
                                </button>
                                <button
                                    onClick={() => navigateLightbox('next')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
                                >
                                    <ChevronRightIcon className="h-8 w-8" />
                                </button>

                                {/* Indicateur */}
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
                                    {lightbox.currentIndex + 1} / {lightbox.images.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewsList;